(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const settings = window.MA_SETTINGS || {};
  const gsap = () => (reduced ? null : window.gsap);

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  const measureHeader = () => {
    const header = document.querySelector('.section-header');
    if (header) document.documentElement.style.setProperty('--ma-header-h', header.offsetHeight + 'px');
  };
  document.addEventListener('DOMContentLoaded', measureHeader);
  window.addEventListener('resize', measureHeader, { passive: true });
  if (designMode) document.addEventListener('shopify:section:load', measureHeader);

  class MaSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.slides.length) return;
      this.index = 0;
      this.delay = Math.max(3, parseFloat(this.dataset.autoplay || '0')) * 1000;
      this.autoplay = !!parseFloat(this.dataset.autoplay || '0') && this.slides.length > 1;
      this.counter = this.querySelector('[data-counter]');
      this.bar = this.querySelector('[data-bar]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.busy = false;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, -1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, 1));
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => {
          const i = parseInt(thumb.dataset.thumb, 10);
          this.go(i, i > this.index ? 1 : -1);
        })
      );

      let startX = null;
      this.addEventListener('pointerdown', (e) => (startX = e.clientX), { passive: true });
      this.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 60) dx < 0 ? this.go(this.index + 1, 1) : this.go(this.index - 1, -1);
        startX = null;
      });

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.visible ? this.play() : this.pause();
        }).observe(this);
      }

      if (designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.findIndex((slide) => slide === e.target || slide.contains(e.target));
          if (i > -1) {
            this.autoplay = false;
            this.go(i, 1, true);
          }
        });
      }

      this.update();
      this.play();
      setTimeout(() => this.classList.remove('is-intro'), 2600);
    }

    play() {
      if (!this.autoplay || this.visible === false) return;
      this.pause();
      if (this.bar) {
        this.bar.style.transition = 'none';
        this.bar.style.transform = 'scaleX(0)';
        void this.bar.offsetWidth;
        this.bar.style.transition = 'transform ' + this.delay + 'ms linear';
        this.bar.style.transform = 'scaleX(1)';
      }
      this.timer = setTimeout(() => this.go(this.index + 1, 1), this.delay);
    }

    pause() {
      clearTimeout(this.timer);
      if (this.bar) {
        const current = getComputedStyle(this.bar).transform;
        this.bar.style.transition = 'none';
        this.bar.style.transform = current === 'none' ? 'scaleX(0)' : current;
      }
    }

    update() {
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => (el.tabIndex = active ? 0 : -1));
      });
    }

    go(target, dir = 1, instant = false) {
      const n = this.slides.length;
      if (n < 2 || this.busy) return;
      const next = (target + n) % n;
      if (next === this.index) return;
      this.classList.remove('is-intro');
      const from = this.slides[this.index];
      const to = this.slides[next];
      this.index = next;
      this.update();

      const g = gsap();
      if (!g || instant) {
        if (window.gsap) this.slides.forEach((s) => window.gsap.set(s, { clearProps: 'all' }));
        this.play();
        return;
      }

      this.busy = true;
      const toMedia = to.querySelector('[data-slide-media]');
      const fromMedia = from.querySelector('[data-slide-media]');
      const toText = to.querySelectorAll('[data-slide-text]');
      const fromText = from.querySelectorAll('[data-slide-text]');
      g.set(to, { autoAlpha: 1, zIndex: 2 });
      g.set(from, { autoAlpha: 1, zIndex: 1 });
      const tl = g.timeline({
        defaults: { ease: 'expo.inOut' },
        onComplete: () => {
          g.set(from, { clearProps: 'all' });
          g.set([...fromText, ...toText], { clearProps: 'all' });
          if (fromMedia) g.set([fromMedia, fromMedia.querySelector('img, svg')], { clearProps: 'all' });
          if (toMedia) g.set([toMedia, toMedia.querySelector('img, svg')], { clearProps: 'all' });
          g.set(to, { clearProps: 'all' });
          this.busy = false;
          this.play();
        },
      });
      if (toMedia) {
        tl.fromTo(
          toMedia,
          { clipPath: dir > 0 ? 'inset(100% 0% 0% 0%)' : 'inset(0% 0% 100% 0%)' },
          { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.3 },
          0
        );
        const img = toMedia.querySelector('img, svg');
        if (img) tl.fromTo(img, { scale: 1.25, yPercent: dir * 8 }, { scale: 1, yPercent: 0, duration: 1.7, ease: 'expo.out' }, 0);
      }
      if (fromMedia) {
        const img = fromMedia.querySelector('img, svg');
        if (img) tl.to(img, { yPercent: -dir * 12, scale: 1.08, duration: 1.3 }, 0);
      }
      if (fromText.length) tl.to(fromText, { autoAlpha: 0, y: -20, duration: 0.45, ease: 'power2.in', stagger: 0.03 }, 0);
      if (toText.length) {
        tl.fromTo(toText, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 1.1, ease: 'expo.out', stagger: 0.07 }, 0.5);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }
  }
  define('ma-slideshow', MaSlideshow);

  class MaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bar = this.querySelector('[data-bar]');
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.refresh(), { passive: true });
      window.addEventListener('resize', () => this.refresh(), { passive: true });
      this.drag();
      this.refresh();
    }

    step(dir) {
      const item = this.track.children[0];
      if (!item) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = item.getBoundingClientRect().width + gap;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + 1) / width));
      this.track.scrollBy({ left: dir * width * perView, behavior: reduced ? 'auto' : 'smooth' });
    }

    refresh() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
      if (this.bar) {
        const visible = this.track.clientWidth / Math.max(1, this.track.scrollWidth);
        const p = max <= 0 ? 1 : visible + (1 - visible) * (x / max);
        this.bar.style.transform = 'scaleX(' + p.toFixed(3) + ')';
      }
      this.classList.toggle('is-static', max <= 2);
    }

    drag() {
      if (!finePointer) return;
      let down = false;
      let moved = false;
      let startX = 0;
      let startLeft = 0;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || e.target.closest('button, input, select, form')) return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          this.track.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        requestAnimationFrame(() => this.track.classList.remove('is-dragging'));
      });
      this.track.addEventListener(
        'click',
        (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        true
      );
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }
  define('ma-carousel', MaCarousel);

  class MaTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.select((i + 1) % this.tabs.length, true);
          if (e.key === 'ArrowLeft') this.select((i - 1 + this.tabs.length) % this.tabs.length, true);
        });
      });
      if (designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.tabs.findIndex((tab) => tab.dataset.blockId === e.detail.blockId);
          if (i > -1) this.select(i);
        });
      }
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      if (focus) this.tabs[index].focus();
      const panel = this.panels[index];
      this.panels.forEach((p) => (p.hidden = p !== panel));
      panel.querySelectorAll('ma-carousel').forEach((c) => c.refresh && c.refresh());
      const g = gsap();
      const items = panel.querySelectorAll('[data-track] > *');
      if (g && items.length) {
        g.fromTo(items, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.9, ease: 'expo.out', stagger: 0.06, clearProps: 'all' });
      }
      if (window.AmbreeMotion) window.AmbreeMotion.refresh();
    }
  }
  define('ma-tabs', MaTabs);

  class MaFinder extends HTMLElement {
    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('[data-option]'));
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.media = Array.from(this.querySelectorAll('[data-media]'));
      this.options.forEach((option, i) => option.addEventListener('click', () => this.select(i)));
      if (designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.options.findIndex((o) => o.dataset.blockId === e.detail.blockId);
          if (i > -1) this.select(i);
        });
      }
    }

    select(index) {
      this.options.forEach((o, i) => o.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
      this.panels.forEach((p, i) => p.classList.toggle('is-active', i === index));
      this.media.forEach((m, i) => m.classList.toggle('is-active', i === index));
    }
  }
  define('ma-finder', MaFinder);

  class MaSizes extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('[data-card]');
      this.pills = Array.from(this.querySelectorAll('[data-variant]'));
      this.pills.forEach((pill) => pill.addEventListener('click', () => this.pick(pill)));
    }

    pick(pill) {
      if (!this.card) return;
      this.pills.forEach((p) => p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'));
      const input = this.card.querySelector('input[name="id"]');
      if (input) input.value = pill.dataset.variant;
      const price = this.card.querySelector('[data-card-price]');
      if (price && pill.dataset.price) price.innerHTML = pill.dataset.price;
      const button = this.card.querySelector('[data-card-add]');
      if (button) {
        const available = pill.dataset.available === 'true';
        button.disabled = !available;
        const label = button.querySelector('span');
        if (label) label.textContent = available ? button.dataset.labelAdd : button.dataset.labelSold;
      }
    }
  }
  define('ma-sizes', MaSizes);

  function cursor() {
    if (!settings.cursor || !finePointer || reduced || designMode) return;
    const dot = document.createElement('div');
    dot.className = 'ma-cursor';
    dot.setAttribute('aria-hidden', 'true');
    dot.innerHTML = '<span></span>';
    document.body.appendChild(dot);
    const label = dot.querySelector('span');
    let x = -100;
    let y = -100;
    let cx = x;
    let cy = y;
    window.addEventListener(
      'pointermove',
      (e) => {
        x = e.clientX;
        y = e.clientY;
        dot.classList.add('is-on');
        const target = e.target.closest ? e.target.closest('[data-cursor], a, button') : null;
        const text = target && target.hasAttribute('data-cursor') ? target.getAttribute('data-cursor') : '';
        dot.classList.toggle('is-label', !!text);
        dot.classList.toggle('is-link', !!target && !text);
        if (label.textContent !== text) label.textContent = text;
      },
      { passive: true }
    );
    document.addEventListener('pointerleave', () => dot.classList.remove('is-on'));
    const loop = () => {
      cx += (x - cx) * 0.2;
      cy += (y - cy) * 0.2;
      dot.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
      requestAnimationFrame(loop);
    };
    loop();
  }

  function backToTop() {
    const button = document.querySelector('[data-back-to-top]');
    if (!button) return;
    const toggle = () => button.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8);
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
    button.addEventListener('click', () => {
      if (window.AmbreeMotion) window.AmbreeMotion.scrollTo(0, { duration: 1.6 });
      else window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    });
  }

  const start = () => {
    cursor();
    backToTop();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
